import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { Button } from '@/shared/ui/button'

const bottomThreshold = 48

function useTranscriptScroll(
  resetKey: string | undefined,
  followRequest: number,
) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const followingRef = useRef(true)
  const lastScrollTopRef = useRef(0)
  const frameRef = useRef<number | null>(null)
  const [pausedAt, setPausedAt] = useState<{
    resetKey?: string
    followRequest: number
  } | null>(null)
  const following =
    !pausedAt ||
    pausedAt.resetKey !== resetKey ||
    pausedAt.followRequest !== followRequest

  const scheduleScroll = useCallback(() => {
    if (frameRef.current !== null) return
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null
      const viewport = viewportRef.current
      if (!viewport || !followingRef.current) return
      viewport.scrollTop = Math.max(
        0,
        viewport.scrollHeight - viewport.clientHeight,
      )
      lastScrollTopRef.current = viewport.scrollTop
    })
  }, [])

  const jumpToLatest = useCallback(() => {
    followingRef.current = true
    setPausedAt(null)
    scheduleScroll()
  }, [scheduleScroll])

  const handleScroll = () => {
    const viewport = viewportRef.current
    if (!viewport) return
    const distance =
      viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop
    const movedUp = viewport.scrollTop < lastScrollTopRef.current
    lastScrollTopRef.current = viewport.scrollTop
    if (distance <= bottomThreshold) {
      followingRef.current = true
      setPausedAt(null)
    } else if (movedUp || !followingRef.current) {
      followingRef.current = false
      setPausedAt({ resetKey, followRequest })
    }
  }

  useEffect(() => {
    followingRef.current = true
    scheduleScroll()
  }, [resetKey, followRequest, scheduleScroll])

  useEffect(() => {
    const viewport = viewportRef.current
    const content = contentRef.current
    if (!viewport || !content) return
    const observer = new ResizeObserver(scheduleScroll)
    observer.observe(viewport)
    observer.observe(content)
    scheduleScroll()

    return () => {
      observer.disconnect()
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [scheduleScroll])

  return { viewportRef, contentRef, following, handleScroll, jumpToLatest }
}

export function AiChatTranscript({
  children,
  resetKey,
  followRequest = 0,
}: {
  children: ReactNode
  resetKey?: string
  followRequest?: number
}) {
  const { t } = useTranslation()
  const scroll = useTranscriptScroll(resetKey, followRequest)

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scroll.viewportRef}
        role="region"
        aria-label={t('aiChat:conversation')}
        tabIndex={0}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain [overflow-anchor:none]"
        onScroll={scroll.handleScroll}
      >
        <div ref={scroll.contentRef} className="space-y-4 p-5">
          {children}
        </div>
      </div>
      {!scroll.following ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="pointer-events-auto shadow-md"
            onClick={scroll.jumpToLatest}
          >
            <ArrowDown className="size-4" aria-hidden="true" />
            {t('aiChat:jumpToLatest')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
